import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { DatasetItemWithInput } from '../../types';
import { LatexText } from '@shared-ui/latex';

export default function EvalDatasetDetail() {
  const { datasetId } = useParams<{ datasetId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPendingOnly, setShowPendingOnly] = useState(false);

  const { data: dataset, isLoading: datasetLoading } = useQuery({
    queryKey: ['evalDataset', datasetId],
    queryFn: () => adminApi.getEvalDataset(datasetId!),
    enabled: !!datasetId,
  });

  const { data: itemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ['datasetItems', datasetId, showPendingOnly],
    queryFn: () => adminApi.getDatasetItems(datasetId!, { pending: showPendingOnly, limit: 100 }),
    enabled: !!datasetId,
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => adminApi.deleteDatasetItem(datasetId!, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasetItems', datasetId] });
      queryClient.invalidateQueries({ queryKey: ['evalDataset', datasetId] });
    },
  });

  if (datasetLoading || itemsLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading dataset...</p>
      </div>
    );
  }

  if (!dataset) {
    return (
      <div className="error-container">
        <p>Dataset not found</p>
        <Link to="/eval/datasets" className="btn btn-secondary">Back to Datasets</Link>
      </div>
    );
  }

  const items = itemsData?.items || [];
  const pendingCount = itemsData?.pendingCount || 0;

  const categoryColors: Record<string, string> = {
    golden: '#f59e0b',
    development: '#3b82f6',
    edge_cases: '#ef4444',
    subject_specific: '#10b981',
  };

  return (
    <div className="dataset-detail-page">
      <div className="breadcrumb">
        <Link to="/eval/datasets">Datasets</Link>
        <span>/</span>
        <span>{dataset.name}</span>
      </div>

      <div className="dataset-header">
        <div className="header-info">
          <span
            className="category-badge"
            style={{ backgroundColor: categoryColors[dataset.category] }}
          >
            {dataset.category.replace('_', ' ')}
          </span>
          <h1>{dataset.name}</h1>
          <p className="readable-id">{dataset.readableId}</p>
          <p className="description">{dataset.description || 'No description'}</p>
        </div>

        <div className="header-stats">
          <div className="stat-card stat-card--fact">
            <span className="stat-card-value">{dataset.metadata.inputCount}</span>
            <span className="stat-card-label">Total Items</span>
          </div>
          <div className="stat-card stat-card--fact">
            <span className="stat-card-value">{dataset.metadata.labeledCount}</span>
            <span className="stat-card-label">Labeled</span>
          </div>
          <div className="stat-card stat-card--fact">
            <span className="stat-card-value">{pendingCount}</span>
            <span className="stat-card-label">Pending</span>
          </div>
        </div>
      </div>

      <div className="actions-bar">
        <div className="filter-actions">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showPendingOnly}
              onChange={(e) => setShowPendingOnly(e.target.checked)}
            />
            Show pending only
          </label>
        </div>
        <div className="add-actions">
          <button className="btn btn-secondary" onClick={() => navigate(`/model-data?datasetId=${datasetId}`)}>
            Import from Production
          </button>
          {pendingCount > 0 && (
            <Link
              to={`/eval/datasets/${datasetId}/label`}
              className="btn btn-primary"
            >
              Start Labeling ({pendingCount} remaining)
            </Link>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <h3>No Items</h3>
          <p>Import inputs from production submissions to get started.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/model-data?datasetId=${datasetId}`)}>
            Import from Production
          </button>
        </div>
      ) : (
        <div className="dt-container--bordered">
          <table className="dt">
            <thead>
              <tr>
                <th>Question</th>
                <th>Subject</th>
                <th>Student</th>
                <th>Student Solution</th>
                <th>Status</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  datasetId={datasetId!}
                  onDelete={() => {
                    if (confirm('Delete this item?')) {
                      deleteItemMutation.mutate(item.id);
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .dataset-detail-page {
          padding: 24px;
        }

        .breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 20px;
          font-size: 14px;
          color: var(--text-secondary);
        }

        .breadcrumb a {
          color: var(--color-accent);
          text-decoration: none;
        }

        .breadcrumb a:hover {
          text-decoration: underline;
        }

        .dataset-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          padding: 24px;
          background: var(--bg-primary);
          border-radius: 12px;
          border: 1px solid var(--border-color);
        }

        .header-info h1 {
          margin: 8px 0 4px;
          font-size: 24px;
          color: var(--text-primary);
        }

        .readable-id {
          margin: 0 0 8px;
          font-family: monospace;
          font-size: 13px;
          color: var(--text-secondary);
        }

        .description {
          margin: 0;
          color: var(--text-secondary);
        }

        .category-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          color: white;
          text-transform: capitalize;
        }

        .header-stats {
          display: flex;
          gap: 16px;
        }

        /* Cards use the shared .stat-card--fact scheme (stat-cards.css). */

        .actions-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .filter-actions {
          display: flex;
          gap: 16px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          cursor: pointer;
          color: var(--text-primary);
        }

        .add-actions {
          display: flex;
          gap: 12px;
        }

        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
        }

        .btn-primary {
          background: var(--color-accent);
          color: white;
        }

        .btn-primary:hover {
          background: #1d4ed8;
        }

        .btn-secondary {
          background: var(--bg-tertiary);
          color: var(--text-primary);
          border: 1px solid var(--border-color-dark);
        }

        .btn-danger {
          background: var(--color-error);
          color: white;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: var(--bg-secondary);
          border-radius: 12px;
          color: var(--text-primary);
        }

        .loading-container, .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
          gap: 16px;
          color: var(--text-primary);
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border-color);
          border-top-color: var(--color-accent);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function ItemRow({
  item,
  datasetId,
  onDelete
}: {
  item: DatasetItemWithInput;
  datasetId: string;
  onDelete: () => void;
}) {
  const input = item.input;
  if (!input) {
    return (
      <tr>
        <td colSpan={7}>Input data not found</td>
      </tr>
    );
  }

  const navigate = useNavigate();
  const isLabeled = item.status === 'labeled';
  const hasSolution = input.studentSolution?.textAnswer || input.studentSolution?.imageUrl || input.studentSolution?.selectedOption;

  return (
    <tr
      className="dt-clickable"
      onClick={() => navigate(`/eval/datasets/${datasetId}/items/${item.id}`)}
    >
      <td>
        <LatexText text={input.question?.text?.trim() ?? ''} truncate={60} />
      </td>
      <td>
        <span className="subject-badge">{input.question?.subject}</span>
      </td>
      <td>{item.studentName || '—'}</td>
      <td>
        {input.studentSolution?.textAnswer && (
          <span className="solution-type">Text</span>
        )}
        {input.studentSolution?.imageUrl && (
          <span className="solution-type">Image</span>
        )}
        {input.studentSolution?.selectedOption && (
          <span className="solution-type">MCQ: {input.studentSolution?.selectedOption}</span>
        )}
        {!hasSolution && <span className="no-solution">No solution</span>}
      </td>
      <td>
        {isLabeled ? (
          <span className="status-badge status-labeled">
            Labeled ({item.labelData?.confidence})
          </span>
        ) : (
          <span className="status-badge status-pending">Pending</span>
        )}
      </td>
      <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
        {item.addedAt ? new Date(item.addedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
      </td>
      <td>
        <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
          Delete
        </button>
      </td>

      <style>{`

        .subject-badge {
          padding: 4px 8px;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 4px;
          font-size: 12px;
          text-transform: capitalize;
        }

        .difficulty-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          text-transform: capitalize;
        }

        .difficulty-easy { background: #d1fae5; color: #065f46; }
        .difficulty-medium { background: #fef3c7; color: #92400e; }
        .difficulty-hard { background: #fee2e2; color: #991b1b; }

        .solution-type {
          display: inline-block;
          padding: 2px 6px;
          background: var(--bg-tertiary);
          border-radius: 4px;
          font-size: 12px;
          margin-right: 4px;
          color: var(--text-primary);
        }

        .no-solution {
          color: var(--text-tertiary);
          font-size: 12px;
        }

        .status-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-labeled {
          background: #d1fae5;
          color: #065f46;
        }

        .status-pending {
          background: #fef3c7;
          color: #92400e;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .btn-sm {
          padding: 4px 10px;
          font-size: 12px;
        }
      `}</style>
    </tr>
  );
}

